import { useQuery } from "@tanstack/react-query";
import { api } from "../api";

export type AdGenerationStatus =
  | "idle"
  | "generating"
  | "ready"
  | "failed"
  | "skipped";

interface OnboardingResponse {
  userMetadata?: {
    adGenerationStatus?: AdGenerationStatus | null;
  } | null;
}

const POLL_INTERVAL_MS = 5000;

const TERMINAL_STATUSES: ReadonlyArray<AdGenerationStatus> = [
  "ready",
  "failed",
  "skipped",
];

/**
 * Polls the onboarding endpoint for adGenerationStatus while the user is on
 * the Ad Review step. Stops polling once the status is terminal. Use
 * `enabled` to only poll while the AdReview component is mounted.
 */
export function useAdGenerationStatus(
  userId: string | undefined,
  enabled: boolean,
) {
  return useQuery<AdGenerationStatus | null>({
    queryKey: ["ad-generation-status", userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      const data: OnboardingResponse = await api.getOnboarding(userId);
      return data.userMetadata?.adGenerationStatus ?? null;
    },
    enabled: !!userId && enabled,
    refetchInterval: (data) =>
      data && TERMINAL_STATUSES.includes(data) ? false : POLL_INTERVAL_MS,
    staleTime: 0,
    // Polling pauses while the tab is in the background; refetching on focus
    // makes the waiting room react the moment the customer switches back
    // instead of up to a full poll interval later.
    refetchOnWindowFocus: (query) =>
      !query.state.data || !TERMINAL_STATUSES.includes(query.state.data),
  });
}
